/**
 * Flash-sale campaign configuration.
 *
 * Edit LAUNCH to go live. Display price must match the Stripe coupon wired via
 * server FLASH_SALE_STRIPE_PROMOTION_CODE_ID.
 */

import {
  BILLING_PLANS,
  PLAN_PRICES,
  formatPlanPriceDisplay,
  getPlanPriceSuffix,
} from '../../components/billing/billingPlanData';

/** Must match server resolveCampaignPromotion allowed id. */
export const DEFAULT_FLASH_SALE_CAMPAIGN_ID = 'flash-sale-pro';

/**
 * Launch knobs — flip these before pointing ads at /flash-sale.
 */
const LAUNCH = {
  enabled: true,
  endsAt: '2026-11-30T23:59:59.000Z',
  /** Must match Stripe coupon before paid traffic. */
  promotionalPrice: 14.99,
  vslUrl: '',
  guaranteeEnabled: false,
};

export const FLASH_SALE_CAMPAIGN = {
  id: DEFAULT_FLASH_SALE_CAMPAIGN_ID,
  name: 'PrecisionTrader Flash Sale',
  enabled: LAUNCH.enabled,
  startsAt: null,
  endsAt: LAUNCH.endsAt,

  copy: {
    announcement: 'Flash sale — Pro at a limited-time price',
    eyebrow: 'Flash sale',
    headline: 'Your Trading Plan Only Works If You Follow It.',
    supportingHeadline:
      'Revenge trading, oversizing, and not tracking your trades is costing you money. Set your rules once, PrecisionTrader holds you to them.',
    supportingHeadlineEmphasis: 'costing you money.',
    promoCallout: 'Limited-time Pro offer',
    primaryCta: 'Claim flash sale',
    secondaryCta: 'See how it works',
    offerHeadline: 'Stop leaving your rules up to willpower.',
    offerSupporting: 'Limited-time Pro pricing for the tools that hold you to your plan.',
    expiredHeadline: 'This flash sale has ended',
    expiredSupporting: 'Standard pricing is still available.',
    expiredCta: 'View pricing',
    guaranteeEnabled: LAUNCH.guaranteeEnabled,
    guaranteeHeadline: 'Try it with confidence',
    guaranteeBody:
      'If PrecisionTrader isn’t right for you, request a refund within 30 days of your eligible purchase, subject to the published guarantee terms.',
    guaranteeDetails:
      'The guarantee period begins on the purchase date. Request refunds through support. See Terms for conditions.',
  },

  media: {
    heroImage: '/images/overview-app-hero.png',
    heroImageAlt: 'PrecisionTrader product overview',
    /** Product overview (no voiceover) — muted autoplay in hero. */
    heroVideo: '/videos/product_overview_no_vo.mp4',
    heroVideoPoster: '/images/overview-app-hero.png',
    howItWorksImages: [
      {
        src: '/images/product_showcase/trading-rules.png',
        alt: 'Trading rules configuration in PrecisionTrader',
      },
      {
        src: '/images/stop-loss-required-blocked.png',
        alt: 'Rule violation blocked during order entry',
      },
      {
        src: '/images/product_showcase/health-dashboard.png',
        alt: 'Behavioral analytics and rule-violation review',
      },
    ],
    featureImages: {
      enforcement: '/images/trade-blocked.png',
      sizing: '/images/position-limit-risk.png',
      journal: '/images/product_showcase/journal-overview-w-sidebar.png',
      analytics: '/images/product_showcase/rule-violations-dashboard.jpg',
    },
    vslUrl: LAUNCH.vslUrl,
    vslPoster: '/images/overview-app-hero.png',
    vslTitle: 'How PrecisionTrader works',
  },

  offer: {
    planTier: 'trade',
    billingInterval: 'monthly',
    promotionalPrice: LAUNCH.promotionalPrice,
    /**
     * 'first_year' | 'first_period' | 'recurring'
     * Stripe coupon duration must match (e.g. 12 months for first_year).
     */
    discountScope: 'first_year',
    renewalPrice: null,
    checkoutCta: 'Claim flash sale',
  },

  cta: {
    registerRedirect: '/flash-sale?checkout=1',
    expiredFallbackPath: '/pricing',
    secondaryScrollTarget: 'flash-sale-vsl',
    offerScrollTarget: 'flash-sale-offer',
  },

  analytics: {
    pageEvent: 'flash_sale_page_view',
    campaignParam: 'campaign',
  },
};

export function getPlanForCampaign(campaign = FLASH_SALE_CAMPAIGN) {
  const tier = campaign.offer?.planTier || 'trade';
  return BILLING_PLANS[tier] || BILLING_PLANS.trade;
}

export function getStandardPrice(campaign = FLASH_SALE_CAMPAIGN) {
  const tier = campaign.offer?.planTier || 'trade';
  const interval = campaign.offer?.billingInterval || 'monthly';
  const prices = PLAN_PRICES[tier] || PLAN_PRICES.pro;
  return interval === 'annual' ? prices.annual : prices.monthly;
}

export function getRenewalPrice(campaign = FLASH_SALE_CAMPAIGN) {
  if (campaign.offer?.renewalPrice != null) return campaign.offer.renewalPrice;
  return getStandardPrice(campaign);
}

export function getDiscountPercent(campaign = FLASH_SALE_CAMPAIGN) {
  const standard = getStandardPrice(campaign);
  const promo = campaign.offer?.promotionalPrice;
  if (promo == null || !standard || promo >= standard) return null;
  return Math.round(((standard - promo) / standard) * 100);
}

export function getSavingsAmount(campaign = FLASH_SALE_CAMPAIGN) {
  const standard = getStandardPrice(campaign);
  const promo = campaign.offer?.promotionalPrice;
  if (promo == null || !standard || promo >= standard) return null;
  return Math.round((standard - promo) * 100) / 100;
}

export function formatOfferPrice(amount) {
  return formatPlanPriceDisplay(amount);
}

export function getBillingSuffix(campaign = FLASH_SALE_CAMPAIGN) {
  return getPlanPriceSuffix(campaign.offer?.billingInterval || 'monthly');
}

export function isOfferConfigured(campaign = FLASH_SALE_CAMPAIGN) {
  const { offer, endsAt, enabled } = campaign;
  if (!enabled) return false;
  if (!endsAt) return false;
  if (offer?.promotionalPrice == null || !Number.isFinite(offer.promotionalPrice)) return false;
  if (!offer?.planTier || !['trade', 'track', 'pro', 'essentials'].includes(offer.planTier)) return false;
  if (!offer?.billingInterval || !['monthly', 'annual'].includes(offer.billingInterval)) return false;
  return true;
}
